<template>
  <div id="app">
    <Toast />
    <CommandPalette v-if="authStore.isAuthenticated" ref="commandPaletteRef" />

    <ApplicationHeader>
      <template #mdps-application-header-title>
        <router-link to="/" class="initiatives-hub-title">Initiatives Hub</router-link>
      </template>

      <template #mdps-application-header-actions-right>
        <template v-if="authStore.isAuthenticated">
          <!-- Unified Search Trigger — opens Command Palette -->
          <button class="header-search-trigger" @click="openCommandPalette" title="Search (⌘K)">
            <MdpsIcon name="search" size="small" class="header-search-trigger-icon" />
            <span class="header-search-trigger-text">Search...</span>
            <kbd class="header-search-trigger-kbd">⌘K</kbd>
          </button>

          <mdps-button
            class="mobile-search-btn"
            type="button"
            variation="float"
            onDark
            icon-only
            icon="search"
            @click="openCommandPalette"
          >Search</mdps-button>

          <CommandCenter />
          <router-link
            v-if="authStore.isAuthenticated && approvalPendingCount > 0"
            to="/approvals"
            class="approval-header-link"
            :aria-label="approvalSummaryLabel"
            :data-tooltip="approvalSummaryLabel"
          >
            <span class="approval-header-link__icon">
              <MdpsIcon name="check" size="small"/>
            </span>
            <span class="approval-header-link__count">{{ approvalPendingCount }}</span>
          </router-link>
          <mdps-button
            class="hamburger-btn"
            type="button"
            variation="float"
            on-dark
            icon-only
            icon="menu"
            @click="mobileNavOpen = !mobileNavOpen"
          >Menu</mdps-button>
        </template>
        <template v-else-if="authStore.isAuthenticated">
          <button class="header-signin-btn" @click="authStore.signout()">Sign Out</button>
        </template>
        <template v-else>
          <button class="header-signin-btn" @click="authStore.signin('/')">Sign In</button>
        </template>
      </template>
    </ApplicationHeader>

    <transition name="fade">
      <div v-if="mobileNavOpen" class="nav-overlay" @click="mobileNavOpen = false" />
    </transition>

    <div class="app-layout">
      <aside v-if="authStore.isAuthenticated" class="app-sidebar" :class="{ 'mobile-open': mobileNavOpen }">
        <MdpsNavigationPanel
          :collapsed="isMobileView ? false : sidebarCollapsed"
          @mdps-navigation-panel-toggle-click="onNavToggle"
          class="nav-toggle"
          :hide-toggle-button="true"
        >
          <MdpsNavigationSection>
            <MdpsNavigationItem
              to="/"
              text="Home"
              icon="home"
              :active="currentPath === '/'"
              @click.native="mobileNavOpen = false"
            />
            <MdpsNavigationItem
              to="/dashboard"
              text="Dashboard"
              icon="dashboard"
              :active="currentPath === '/dashboard'"
              @click.native="mobileNavOpen = false"
            />
            <MdpsNavigationItem
              to="/search"
              text="Search"
              icon="search"
              :active="currentPath === '/search'"
              @click.native="mobileNavOpen = false"
            />
            <MdpsNavigationItem
                v-if="authStore.canCreateInitiative"
                to="/submit"
                text="Submit Initiative"
                icon="plus"
                :active="currentPath === '/submit'"
                @click.native="mobileNavOpen = false"
            />
            <button v-else class="navigation-disabled-item" type="button" @click="handleRestrictedSubmitClick">
              <MdpsIcon name="plus" size="small"/>
              <span>Submit Initiative</span>
            </button>

            <MdpsNavigationItem
              to="/leaderboard"
              text="Leaderboard"
              icon="chart-bar"
              :active="currentPath === '/leaderboard'"
              @click.native="mobileNavOpen = false"
            />
            <MdpsNavigationItem
              to="/profile"
              text="Profile"
              icon="user"
              :active="currentPath === '/profile'"
              @click.native="mobileNavOpen = false"
            />
            <MdpsNavigationItem
              v-if="authStore.canViewUsers"
              to="/admin"
              text="Admin"
              icon="settings"
              :active="currentPath.startsWith('/admin')"
              @click.native="mobileNavOpen = false"
            />
            <MdpsNavigationItem
              to="/contact"
              text="Contact"
              icon="chat-bubble"
              :active="currentPath === '/contact'"
              @click.native="mobileNavOpen = false"
            />
          </MdpsNavigationSection>
        </MdpsNavigationPanel>
      </aside>

      <main class="main-content">
        <SmartBreadcrumbs v-if="authStore.isAuthenticated" />
        <ErrorBoundary>
          <router-view />
        </ErrorBoundary>
      </main>
    </div>
    <QuickActionsFab v-if="authStore.isAuthenticated" />
    <SupportWidget />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import ApplicationHeader from '@mdps/application-header-vue3'
import {
  MdpsNavigationPanel,
  MdpsNavigationSection,
  MdpsNavigationItem
} from '@mdps/navigation-vue3'
import MdpsIcon from '@mdps/icon-vue3'
import { MdpsButton } from '@mdps/button-vue3'
import Toast from '@/components/Toast.vue'
import CommandCenter from '@/components/CommandCenter.vue'
import CommandPalette from '@/components/CommandPalette.vue'
import SmartBreadcrumbs from '@/components/SmartBreadcrumbs.vue'
import QuickActionsFab from '@/components/QuickActionsFab.vue'
import ErrorBoundary from '@/components/ErrorBoundary.vue'
import SupportWidget from '@/components/SupportWidget.vue'
import '@/styles/fonts.css'
import { useAuthStore } from '@/stores/authStore.js'
import { showContributorUpgradeMessage } from '@/utils/roleMessages.js'
import { approvalsAPI } from '@/services/api.js'

const authStore = useAuthStore()

const route = useRoute()
const sidebarCollapsed = ref(false)
const mobileNavOpen = ref(false)
const isMobileView = ref(window.innerWidth <= 768)
const commandPaletteRef = ref(null)
const approvalPendingCount = ref(0)
let approvalPollTimer = null

function openCommandPalette() {
  commandPaletteRef.value?.open()
}

function handleRestrictedSubmitClick() {
  mobileNavOpen.value = false
  showContributorUpgradeMessage()
}

const currentPath = computed(() => route.path)
const approvalSummaryLabel = computed(() =>
  `${approvalPendingCount.value} pending approval${approvalPendingCount.value === 1 ? '' : 's'}`
)

function onNavToggle() {
  if (window.innerWidth <= 768) {
    mobileNavOpen.value = false
  } else {
    sidebarCollapsed.value = !sidebarCollapsed.value
  }
}

function onResize() {
  isMobileView.value = window.innerWidth <= 768
}

watch(() => route.fullPath, () => {
  mobileNavOpen.value = false
  refreshApprovalSummary()
})

onMounted(() => {
  window.addEventListener('resize', onResize)
  window.addEventListener('approvals:updated', refreshApprovalSummary)
  refreshApprovalSummary()
  approvalPollTimer = window.setInterval(refreshApprovalSummary, 60000)
})

onUnmounted(() => {
  window.removeEventListener('resize', onResize)
  window.removeEventListener('approvals:updated', refreshApprovalSummary)
  if (approvalPollTimer) window.clearInterval(approvalPollTimer)
})

async function refreshApprovalSummary() {
  if (!authStore.isAuthenticated || !authStore.isAuthenticated.value) return
  try {
    const response = await approvalsAPI.summary()
    approvalPendingCount.value = response.data?.pendingCount || 0
  } catch {
    approvalPendingCount.value = 0
  }
}
</script>

<style>
/* ═══════════════════════════════════════════════════════════════════
  MORNINGSTAR INITIATIVES HUB — Design System Foundation
   ═══════════════════════════════════════════════════════════════════ */

body,
body * {
  font-family: "MorningstarIntrinsic", HelveticaNeue, "Helvetica Neue", Helvetica, Arial, sans-serif;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body {
  height: 100%;
  overflow: hidden;
}

#app {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
}

.app-layout {
  display: flex;
  flex: 1;
  overflow: hidden;
  min-height: 0;
}

/* ─── Sidebar Navigation ──────────────────────────────────────── */
.app-sidebar {
  flex-shrink: 0;
  width: 264px;
  min-width: 264px;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  background: #171717;
  border-right: 1px solid rgba(255, 255, 255, 0.06);
  transition: transform 0.3s ease;
  padding-right: 0;
  box-sizing: border-box;
  scrollbar-width: thin;
  scrollbar-color: rgba(255,255,255,0.1) transparent;
}

.app-sidebar :deep(nav),
.app-sidebar :deep(.mdps-navigation-panel),
.app-sidebar :deep(.mdps-navigation__panel) {
  background: transparent !important;
}

.app-sidebar :deep(a),
.app-sidebar :deep(button) {
  border-radius: 8px !important;
  margin: 0.18rem 0.72rem !important;
  min-height: 40px;
  color: rgba(255, 255, 255, 0.68) !important;
  font-weight: 650 !important;
}

.app-sidebar :deep(a:hover),
.app-sidebar :deep(button:hover) {
  background: rgba(255, 255, 255, 0.075) !important;
  color: #ffffff !important;
}

.app-sidebar :deep(a[aria-current="page"]),
.app-sidebar :deep(.active),
.app-sidebar :deep([data-active="true"]) {
  background: rgba(255, 255, 255, 0.09) !important;
  color: #ffffff !important;
  box-shadow: inset 2px 0 0 rgba(255, 255, 255, 0.72);
}

/* Sidebar icons — force white on dark background */
.app-sidebar svg {
  stroke: white !important;
  fill: white !important;
}

.nav-bottom-divider {
  margin: 0.75rem 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.navigation-disabled-item {
  width: calc(100% - 1rem);
  margin: 0.125rem 0.5rem;
  padding: 0.68rem 0.85rem;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: rgba(255, 255, 255, 0.38);
  cursor: not-allowed;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font: inherit;
  font-size: 0.88rem;
  text-align: left;
}

.navigation-disabled-item:hover {
  background: rgba(255, 255, 255, 0.04);
}

.navigation-disabled-item svg {
  opacity: 0.45;
}

/* ─── Main Content ─────────────────────────────────────────────── */
.main-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0;
  min-width: 0;
  background: var(--ih-bg, #f7f7f8);
}

/* ─── Header Title ─────────────────────────────────────────────── */
.initiatives-hub-title {
  text-decoration: none;
  color: #ffffff;
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: 0.01em;
  white-space: nowrap;
  transition: opacity 0.15s;
}

.initiatives-hub-title:hover {
  opacity: 0.85;
}

/* ─── Search Trigger (Header) ──────────────────────────────────── */
.header-search-trigger {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.48rem 0.78rem;
  margin-right: 0.5rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.72);
  cursor: pointer;
  font-size: 0.8rem;
  font-family: inherit;
  transition: all 0.15s ease;
  min-width: 220px;
  box-shadow: none;
}

.header-search-trigger:hover {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.25);
  color: rgba(255, 255, 255, 0.9);
}

.header-search-trigger-icon {
  color: inherit;
  flex-shrink: 0;
}

.header-search-trigger-text {
  flex: 1;
  text-align: left;
  font-size: 0.8rem;
  opacity: 0.8;
}

.header-search-trigger-kbd {
  padding: 0.15rem 0.4rem;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.15);
  font-family: -apple-system, BlinkMacSystemFont, monospace;
  font-size: 0.62rem;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1;
}

.approval-header-link {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  min-height: 36px;
  margin-right: 0.5rem;
  padding: 0.25rem 0.45rem 0.25rem 0.35rem;
  border: 1px solid rgba(247, 185, 85, 0.45);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  color: #ffffff;
  text-decoration: none;
  font-size: 0.82rem;
  font-weight: 800;
  box-shadow: none;
  transition: transform 140ms ease, border-color 140ms ease, background 140ms ease, box-shadow 140ms ease;
}

.approval-header-link:hover {
  transform: translateY(-1px);
  border-color: rgba(247, 185, 85, 0.75);
  background: rgba(255, 255, 255, 0.12);
  box-shadow: none;
}

.approval-header-link::after {
  content: attr(data-tooltip);
  position: absolute;
  top: calc(100% + 9px);
  right: 0;
  min-width: max-content;
  max-width: 220px;
  padding: 0.42rem 0.6rem;
  border: 1px solid rgba(28, 37, 47, 0.08);
  border-radius: 7px;
  background: #ffffff;
  color: #263746;
  font-size: 0.76rem;
  font-weight: 700;
  line-height: 1.2;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
  opacity: 0;
  pointer-events: none;
  transform: translateY(-3px);
  transition: opacity 130ms ease, transform 130ms ease;
  z-index: 1200;
}

.approval-header-link:hover::after,
.approval-header-link:focus-visible::after {
  opacity: 1;
  transform: translateY(0);
}

.approval-header-link__icon,
.approval-header-link__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.approval-header-link__icon {
  width: 25px;
  height: 25px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  color: #1f2a33;
}

.approval-header-link__count {
  min-width: 20px;
  height: 20px;
  border-radius: 999px;
  background: #f7b955;
  color: #1f2a33;
  padding: 0 0.35rem;
}

/* ─── Header Sign-In Button ────────────────────────────────────── */
.header-signin-btn {
  padding: 0.45rem 1.1rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  cursor: pointer;
  font-size: 0.82rem;
  font-weight: 600;
  font-family: inherit;
  transition: all 0.15s ease;
}

.header-signin-btn:hover {
  background: rgba(255, 255, 255, 0.18);
  border-color: rgba(255, 255, 255, 0.5);
}

/* ─── Hamburger & Mobile Buttons ───────────────────────────────── */
.hamburger-btn,
.mobile-search-btn {
  display: none !important;
}

/* ─── Overlay ──────────────────────────────────────────────────── */
.nav-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  z-index: 899;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* ─── Tablet Breakpoint (768–1024px) ──────────────────────────── */
@media (max-width: 1024px) and (min-width: 769px) {
  .app-sidebar {
    width: 200px;
    min-width: 200px;
  }

  .header-search-trigger {
    min-width: 150px;
  }
}

/* ─── Mobile Breakpoint (≤768px) ──────────────────────────────── */
@media (max-width: 768px) {
  .app-sidebar {
    position: fixed;
    top: 47px;
    left: 0;
    right: 0;
    width: 100vw;
    min-width: unset;
    height: auto;
    max-height: 0;
    overflow: hidden;
    z-index: 900;
    transition: max-height 0.35s ease, opacity 0.3s ease;
    opacity: 0;
    pointer-events: none;
  }

  .app-sidebar.mobile-open {
    max-height: calc(100vh - 47px);
    overflow-y: auto;
    opacity: 1;
    pointer-events: all;
  }

  .app-sidebar nav,
  .app-sidebar.mobile-open nav {
    width: 100% !important;
    min-width: 100% !important;
    max-width: 100% !important;
    height: auto !important;
    min-height: unset !important;
  }

  .app-sidebar nav > * {
    width: 100% !important;
  }

  .app-sidebar :deep(.mdps-navigation__panel) {
    width: 100%;
    border-radius: 0;
  }

  .main-content {
    width: 100%;
    padding: 0;
  }

  .header-search-trigger {
    display: none;
  }

  .hamburger-btn,
  .mobile-search-btn {
    display: flex !important;
  }

  .approval-header-link {
    min-height: 32px;
    padding: 0 0.5rem;
  }
}

/* ─── Small Mobile (≤480px) ───────────────────────────────────── */
@media (max-width: 480px) {
  .initiatives-hub-title {
    font-size: 0.9rem;
  }
}
</style>
